import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, CreditCard, Tag, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { items, updateQuantity, removeItem, total, count } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code: couponCode, subtotal: total });
      setAppliedCoupon(res.data);
      setCouponCode('');
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Cupon invalido');
      setAppliedCoupon(null);
    }
    setCouponLoading(false);
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCouponError(''); };

  const discount = appliedCoupon?.discount || 0;
  const finalTotal = Math.max(total - discount, 0);

  const handleCheckout = async () => {
    if (!user) { navigate('/auth'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        items: items.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        origin_url: window.location.origin,
        coupon_code: appliedCoupon?.code || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al procesar el pago');
    }
    setLoading(false);
  };

  if (count === 0) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center" data-testid="cart-empty">
        <div className="text-center space-y-4">
          <ShoppingBag className="h-16 w-16 text-[var(--dark)]/20 mx-auto" />
          <h2 className="text-2xl font-bold text-[var(--dark)]">Tu carrito esta vacio</h2>
          <p className="text-[var(--dark)]/50">Descubre nuestros productos de skincare coreano</p>
          <Link to="/tienda"><Button className="bg-[var(--violet)] text-white rounded-full px-8 mt-4" data-testid="shop-now-btn">Ir a la tienda</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="cart-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/tienda" className="inline-flex items-center gap-2 text-sm text-[var(--dark)]/50 hover:text-[var(--violet)] mb-6">
          <ArrowLeft className="h-4 w-4" /> Seguir comprando
        </Link>

        <h1 className="text-3xl font-bold text-[var(--dark)] mb-8">Tu Carrito ({count})</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map(item => (
              <div key={item.product_id} className="bg-white rounded-2xl p-4 flex gap-4 border border-[var(--sage)]/20" data-testid={`cart-item-${item.product_id}`}>
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-[var(--cream)] flex-shrink-0">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-[var(--dark)] line-clamp-1">{item.name}</h3>
                  <p className="text-sm text-[var(--violet)] font-bold mt-1">${item.price?.toFixed(2)}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center border border-[var(--sage)] rounded-full">
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-7 text-center text-sm font-medium">{item.quantity}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => removeItem(item.product_id)} data-testid="remove-item-btn">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="bg-white rounded-2xl p-6 border border-[var(--sage)]/20 h-fit sticky top-24">
            <h3 className="text-lg font-bold text-[var(--dark)] mb-4">Resumen</h3>

            {/* Coupon Section */}
            <div className="mb-4 pb-4 border-b border-[var(--sage)]/20">
              {appliedCoupon ? (
                <div className="flex items-center justify-between bg-[var(--violet-light)] rounded-xl p-3" data-testid="applied-coupon">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-[var(--violet)]" />
                    <div>
                      <p className="text-xs font-bold text-[var(--violet)]">{appliedCoupon.code}</p>
                      <p className="text-[10px] text-[var(--violet)]/70">
                        {appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}% descuento` : `$${appliedCoupon.discount_value} descuento`}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-[var(--violet)] hover:text-red-500" onClick={removeCoupon} data-testid="remove-coupon-btn">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                      placeholder="Codigo de cupon"
                      className="h-9 text-sm uppercase flex-1"
                      onKeyDown={e => { if (e.key === 'Enter') applyCoupon(); }}
                      data-testid="coupon-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9 text-xs border-[var(--violet)]/30 text-[var(--violet)] hover:bg-[var(--violet-light)]"
                      onClick={applyCoupon}
                      disabled={couponLoading}
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Aplicar'}
                    </Button>
                  </div>
                  {couponError && <p className="text-[10px] text-red-500 mt-1.5" data-testid="coupon-error">{couponError}</p>}
                </div>
              )}
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between text-[var(--dark)]/60">
                <span>Subtotal ({count} items)</span>
                <span>${total.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-600" data-testid="discount-line">
                  <span>Descuento</span>
                  <span>-${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-[var(--dark)]/60">
                <span>Envio</span>
                <span className="text-[var(--sage-dark)] font-medium">Gratis</span>
              </div>
              <hr className="border-[var(--sage)]/20" />
              <div className="flex justify-between text-lg font-bold text-[var(--dark)]">
                <span>Total</span>
                <span data-testid="cart-total">${finalTotal.toFixed(2)}</span>
              </div>
            </div>
            <Button
              className="w-full mt-6 h-12 bg-[var(--violet)] hover:bg-[var(--violet-dark)] text-white rounded-xl text-base font-semibold btn-shimmer"
              onClick={handleCheckout}
              disabled={loading}
              data-testid="checkout-btn"
            >
              <CreditCard className="mr-2 h-5 w-5" />
              {loading ? 'Procesando...' : user ? 'Pagar ahora' : 'Ingresar para pagar'}
            </Button>
            <p className="text-xs text-center text-[var(--dark)]/30 mt-3">Pago seguro con Stripe</p>
          </div>
        </div>
      </div>
    </div>
  );
}
